import type { EventTicket } from '@mmef/shared/api';

/** "25–35", "30 and over", "up to 25", or null when the ticket names no age at all. */
function describeTicketAges({ minAge, maxAge }: EventTicket): string | null {
  if (minAge === null && maxAge === null) return null;
  if (minAge !== null && maxAge !== null) return `${minAge}–${maxAge}`;
  if (minAge !== null) return `${minAge} and over`;
  return `up to ${maxAge}`;
}

function formatAmount(minor: number, currency: string): string {
  if (minor === 0) return 'Free';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
  }).format(minor / 100);
}

/**
 * Every ticket an event sells.
 *
 * <p>Only on the event's own page. Search results show a single figure, because a table of results
 * has no room to explain that £10 buys a chaperone a seat rather than a place — and a listing that
 * appeared to cost £10 when participation costs £35 would be actively misleading.
 *
 * <p>Arrives already grouped by kind with companion rates last, so this renders the order it's
 * given rather than sorting again and risking a different answer from the server's.
 */
export function TicketList({ tickets }: { tickets: EventTicket[] }) {
  if (tickets.length === 0) return null;

  const companions = tickets.filter((t) => t.attendeeType === 'COMPANION');

  return (
    <section className="section" id="tickets" data-section="Tickets">
      <h2>Tickets</h2>
      <ul className="ticket-list">
        {tickets.map((ticket, index) => {
          const ages = describeTicketAges(ticket);
          return (
            <li className="ticket" key={`${ticket.label}-${ticket.minAge}-${index}`}>
              <div className="ticket__what">
                <span className="ticket__label">{ticket.label}</span>
                {ages && <span className="badge">{ages}</span>}
                {ticket.attendeeType === 'COMPANION' && (
                  <span className="badge">Accompanying</span>
                )}
              </div>
              <span className="ticket__price">
                {formatAmount(ticket.amountMinor, ticket.currency)}
              </span>
            </li>
          );
        })}
      </ul>

      {companions.length > 0 && (
        <p className="field__hint">
          Accompanying tickets are for a mehram or chaperone attending with someone rather than
          taking part, so they aren&apos;t counted in the price shown on the search page.
        </p>
      )}

      <p className="field__hint">
        Prices as the organiser gave them. Buy through the organiser&apos;s own booking link — we
        don&apos;t sell tickets and never take payment.
      </p>
    </section>
  );
}
